import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ChevronDown, Search } from "lucide-react";
import { API } from "./apiClient";

let cache = null;
let logoCache = {};
let inflight = null;
const listeners = new Set();

const trLower = (s) => String(s || "").replace(/I/g, "ı").replace(/İ/g, "i").toLowerCase().replace(/\s+/g, " ").trim();
const base = (s) => trLower(s).replace(/[.,]/g, " ").replace(/\b(a ?ş|aş|anonim şirketi|sigortacılık|sigorta|sig)\b/g, " ").replace(/\s+/g, " ").trim();

const DOMAINS = {
  "acıbadem": "bupa.com", "ak": "aksigorta.com.tr", "allianz": "allianz.com.tr", "anadolu": "anadolusigorta.com.tr",
  "ankara": "ankarasigorta.com.tr", "arex": "arexsigorta.com.tr", "aveon global": "aveonglobalsigorta.com",
  "axa": "axa.com", "bereket": "bereketsigorta.com.tr", "corpus": "corpus.com.tr", "doğa": "dogasigorta.com",
  "eureko": "eurekosigorta.com.tr", "generali": "generali.it", "groupama": "groupama.com.tr",
  "güneş": "turkiyesigorta.com.tr", "hdı": "hdi.global", "hepiyi": "hepiyi.com.tr", "koru": "korusigorta.com.tr",
  "magdeburger": "magdeburger.com.tr", "mapfre": "mapfre.com.tr", "neova": "neova.com.tr", "orient": "orientsigorta.com.tr",
  "quick": "quicksigorta.com", "ray": "raysigorta.com.tr", "sompo": "sompojapan.co.jp", "prive": "prive.com.tr", "dubai starr": "starrcompanies.com",
  "türkiye": "turkiyesigorta.com.tr", "unico": "unicosigorta.com.tr", "zurich": "zurich.com",
  "bupa acıbadem": "bupa.com", "allianz yaşam ve emeklilik": "allianz.com.tr",
};

const PALETTE = ["#1a5fe0", "#0f172a", "#2563eb", "#1447b8", "#334155", "#3b82f6", "#1e40af"];

export function canonCompany(name, list) {
  if (!String(name || "").trim()) return "";
  const b = base(name);
  return (list || []).find((c) => base(c) === b) || String(name).trim();
}

export function setCompaniesCache(list, logos) {
  cache = list;
  if (logos) logoCache = logos;
  listeners.forEach((fn) => fn({ list, logos: logoCache }));
}

function useCompanyStore() {
  const [state, setState] = useState({ list: cache || [], logos: logoCache });
  useEffect(() => {
    listeners.add(setState);
    if (!cache) {
      inflight ||= axios.get(`${API}/user/preferences`).then(({ data }) => setCompaniesCache(data.companies || [], data.companyLogos || {})).finally(() => { inflight = null; });
    }
    return () => listeners.delete(setState);
  }, []);
  return state;
}

export const useCompanies = () => useCompanyStore().list;
export const useCompanyLogos = () => useCompanyStore().logos;

const absUrl = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export function companyLogoSources(name, logos) {
  const own = logos?.[name] || Object.entries(logos || {}).find(([k]) => base(k) === base(name))?.[1];
  const domain = DOMAINS[base(name)];
  return [own && absUrl(own), domain && `https://www.google.com/s2/favicons?domain=${domain}&sz=64`, domain && `https://icons.duckduckgo.com/ip3/${domain}.ico`].filter(Boolean);
}

export function CompanyLogo({ name, size = 22, testId }) {
  const { logos } = useCompanyStore();
  const sources = companyLogoSources(name, logos);
  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [sources[0]]); // eslint-disable-line react-hooks/exhaustive-deps
  const style = { width: size, height: size, fontSize: Math.max(9, size * 0.42) };
  if (!name) return null;
  if (idx < sources.length) {
    return <img className="company-logo" src={sources[idx]} alt="" style={style} onError={() => setIdx((i) => i + 1)} onLoad={(e) => { if (e.currentTarget.naturalWidth <= 16 && sources[idx].includes("google.com/s2")) setIdx((i) => i + 1); }} data-testid={testId} />;
  }
  const b = base(name) || name;
  const initials = b.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toLocaleUpperCase("tr-TR")).join("");
  const color = PALETTE[[...b].reduce((s, ch) => s + ch.charCodeAt(0), 0) % PALETTE.length];
  return <span className="company-logo company-logo-fallback" style={{ ...style, background: color }} data-testid={testId}>{initials}</span>;
}

export function CompanyName({ name, size = 20 }) {
  if (!name) return <em className="dim">—</em>;
  return <span className="company-name-cell"><CompanyLogo name={name} size={size} />{name}</span>;
}

export function CompanySelect({ label = "Sigorta şirketi", name = "company", value, onChange, required, testId }) {
  const list = useCompanies();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const id = testId || `${name}-select`;
  const options = value && !list.includes(value) ? [value, ...list] : list;
  const shown = options.filter((c) => trLower(c).includes(trLower(q)));
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const pick = (c) => { onChange({ target: { name, value: c } }); setOpen(false); setQ(""); };
  return (
    <div className="field company-select" ref={ref}>
      <span>{label}</span>
      <button type="button" className="company-select-trigger" onClick={() => setOpen((o) => !o)} data-testid={id}>
        {value ? <CompanyName name={value} /> : <span className="company-select-placeholder">Şirket seçin</span>}
        <ChevronDown size={14} />
      </button>
      <input className="company-select-required" tabIndex={-1} value={value || ""} onChange={() => {}} required={required} aria-hidden="true" />
      {open && (
        <div className="company-select-menu" data-testid={`${id}-menu`}>
          <div className="company-select-search"><Search size={13} /><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Şirket ara…" data-testid={`${id}-search`} /></div>
          <div className="company-select-list">
            {value && !required && <button type="button" className="company-select-option dim" onClick={() => pick("")} data-testid={`${id}-clear`}>Seçimi temizle</button>}
            {shown.map((c) => (
              <button type="button" key={c} className={`company-select-option${c === value ? " active" : ""}`} onClick={() => pick(c)} data-testid={`${id}-option-${c}`}>
                <CompanyName name={c} />
              </button>
            ))}
            {shown.length === 0 && <div className="company-select-empty">Sonuç yok</div>}
          </div>
        </div>
      )}
    </div>
  );
}
